import {$$} from '@vue-macros/reactivity-transform/macros'

import {
  IDatabasePropertyResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'

import type {
  ITimeTrackerPropertyTypeInterfaces,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/TimeTrackerPropertyTypeStrategy/types/ITimeTrackerPropertyTypeInterfaces'
import {PropertyStrategyComponentType} from '@toolify/client/src/modules/tableItems/enum/PropertyStrategyComponentType'
import {
  IChartDashboardItemTypeInterfaces,
} from '@toolify/server/src/services/DashboardItemService/strategies/DashboardItemTypeStrategies/ChartDashboardItemTypeStrategy/types/IChartDashboardItemTypeInterfaces'
import {
  buildTimeTrackerValueAxisTicks,
  isTimeTrackerValueAxis,
} from '@toolify/client/src/singletons/dashboard/timeTrackerValueAxisTicks'
import {
  findDatabaseRowPropertyMetaDataByDatasetIndex,
} from '@toolify/client/src/singletons/dashboard/findDatabaseRowPropertyMetaDataByDatasetIndex'
import {
  shouldGetDisplayValueByAggregationType,
} from '@toolify/client/src/singletons/dashboard/shouldGetDisplayValueByAggregationType'

export function useTimeTrackerValueTypeStrategy() {
  const strategyComponentType = PropertyStrategyComponentType.Dropdown

  const fullValueToText = (value: ITimeTrackerPropertyTypeInterfaces['fullValue'], property: IDatabasePropertyResponseInstance) => {
    if(!value) {
      return ''
    }
    
    const timeOfStartedItems = Math.floor(value.startDatesOfStartedItems.map(date => {
      const time = new Date().getTime() - new Date(date).getTime() 
      return time > 0 ? time : 0
    }).reduce((a, b) => {
      return a + b
    }, 0) / 1000)

    const totalTime = value.timeOfEndedItems + timeOfStartedItems
    const hours = Math.floor(totalTime / 3600)
    const minutes = Math.floor((totalTime % 3600) / 60)
    const seconds = Math.floor(totalTime % 60)
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }

  const getDisplayValue = (numberOfMinutes: number, propertyOptions: ITimeTrackerPropertyTypeInterfaces['options']) => {
    if(isNaN(Number(numberOfMinutes)) || [null, undefined].includes(numberOfMinutes)) {
      return numberOfMinutes
    }

    const value = Number(numberOfMinutes)
    const hours = Math.floor(value / 60)
    const minutes = Math.floor((value % 60))
    return `${hours}:${String(minutes).padStart(2, '0')}`
  }

  // A chart values axis made purely of tracked-time measures counts raw minutes — these hooks put
  // its ticks in the same H:MM format as the rest of the chart and land them on round durations.
  const getValueAxisTickFormatter = (
    chartOptions: IChartDashboardItemTypeInterfaces['options'],
    rowDatabaseProperties: IDatabasePropertyResponseInstance[],
  ): ((value: unknown) => string) | null => {
    if(!isTimeTrackerValueAxis(rowDatabaseProperties)) {
      return null
    }
    const rowPropertyId = rowDatabaseProperties[0].id
    return (value: unknown) => {
      const {
        databaseRowPropertyOptions,
        databaseRowPropertyAggregationType,
      } = findDatabaseRowPropertyMetaDataByDatasetIndex(rowPropertyId, chartOptions?.rowPropertiesPayload, rowDatabaseProperties)
      if(!shouldGetDisplayValueByAggregationType(databaseRowPropertyAggregationType)) {
        return String(value)
      }
      return String(getDisplayValue(value as number, databaseRowPropertyOptions as ITimeTrackerPropertyTypeInterfaces['options']))
    }
  }

  return $$({
    fullValueToText,
    getDisplayValue,
    getValueAxisTickFormatter,
    buildValueAxisTicks: buildTimeTrackerValueAxisTicks,
    strategyComponentType,
  })
}
